import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword } from "firebase/auth";
import { IoAlertCircleOutline, IoCheckmarkCircleOutline } from "react-icons/io5";
import { auth } from "../../config/firebase";
import PasswordField from "./PasswordField";
import PasswordRequirements, { usePasswordValidation } from "./PasswordRequirements";

const AlertIcon = IoAlertCircleOutline as any;
const CheckIcon = IoCheckmarkCircleOutline as any;

// Signed-in password change. Firebase requires a recent sign-in for updatePassword,
// so we always re-authenticate with the current password first.
// Rendered into document.body: the NavBar that opens it has a backdrop blur, which
// would otherwise make this "fixed inset-0" overlay only as big as the bar.
const ChangePasswordDialog: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const [current, setCurrent] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const { status, unavailable } = usePasswordValidation(password);
  const meetsPolicy = unavailable || !!status?.isValid;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && !saving && close();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, saving]);

  if (!open) return null;

  const close = () => {
    setCurrent("");
    setPassword("");
    setConfirm("");
    setError("");
    setDone(false);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    const user = auth.currentUser;
    if (!user?.email) return;
    if (!meetsPolicy) {
      setError("Your new password doesn't meet all the requirements.");
      return;
    }
    if (password !== confirm) {
      setError("The new passwords don't match.");
      return;
    }
    if (password === current) {
      setError("Your new password must be different from your current one.");
      return;
    }
    try {
      setSaving(true);
      await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, current));
      await updatePassword(user, password);
      setDone(true);
    } catch (err: any) {
      console.error("Password change failed:", err);
      if (err?.code === "auth/invalid-credential" || err?.code === "auth/wrong-password") {
        setError("Your current password is incorrect.");
      } else if (err?.code === "auth/password-does-not-meet-requirements") {
        setError("Your new password doesn't meet all the requirements.");
      } else if (err?.code === "auth/too-many-requests") {
        setError("Too many attempts. Please wait a few minutes and try again.");
      } else {
        setError("Couldn't change your password. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-scrim/40" onClick={saving ? undefined : close} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="change-password-title"
        className="relative w-full max-w-md max-h-[calc(100vh-2rem)] overflow-y-auto rounded-2xl bg-surface p-6 shadow-2xl"
      >
        <h3 id="change-password-title" className="mb-4 text-lg font-semibold text-ink">
          Change password
        </h3>

        {done ? (
          <>
            <div className="mb-5 flex items-start gap-2 rounded-xl bg-brand-50 border border-brand-500/30 px-4 py-3 text-sm text-brand-700">
              <CheckIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <span>Your password has been changed.</span>
            </div>
            <div className="flex justify-end">
              <button
                onClick={close}
                className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-on-brand hover:bg-brand-600"
              >
                Done
              </button>
            </div>
          </>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="flex items-start gap-2 rounded-xl bg-bad-soft border border-bad-line px-4 py-3 text-sm text-bad-ink">
                <AlertIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}
            <input type="email" value={auth.currentUser?.email ?? ""} autoComplete="username" readOnly hidden />
            <PasswordField
              id="current-password"
              label="Current password"
              value={current}
              onChange={setCurrent}
              autoComplete="current-password"
              disabled={saving}
              autoFocus
            />
            <PasswordField
              id="new-password"
              label="New password"
              value={password}
              onChange={setPassword}
              autoComplete="new-password"
              disabled={saving}
            />
            <PasswordRequirements status={status} />
            <PasswordField
              id="confirm-password"
              label="Confirm new password"
              value={confirm}
              onChange={setConfirm}
              autoComplete="new-password"
              disabled={saving}
            />
            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={close}
                disabled={saving}
                className="rounded-lg px-4 py-2 text-sm font-medium text-ink-muted hover:bg-paper-100 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving || !current || !meetsPolicy || !confirm}
                className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-on-brand hover:bg-brand-600 disabled:opacity-50"
              >
                {saving ? "Saving…" : "Change password"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>,
    document.body
  );
};

export default ChangePasswordDialog;
